import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { DropdownMenu } from "radix-ui";

const links = [
  { name: "Home", path: "/home" },
  { name: "Events", path: "/events" },
  { name: "RSVP", path: "/rsvp" },
  { name: "Gallery", path: "/gallery" },
  { name: "Hotels", path: "/hotels" },
  { name: "Q&A", path: "/qa" },
  { name: "Registry", path: "/registry" },
];

export default function Navbar() {
  const [mounted, setMounted] = useState(false)
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoverGallery, setHoverGallery] = useState(false)
  const location = useLocation();
  const isHome = location.pathname.startsWith("/home");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    setMounted(true);
  }, []);

  function customMobileMenu() {
    return (
      <>
        <div className={`flex md:hidden items-center justify-between px-4 py-3 transition-all duration-300 ${(menuOpen || (!(isHome && !scrolled))) ? "bg-white border-gray-200 border-b" : "border-transparent"}`}>
          <div onClick={() => setMenuOpen(!menuOpen)} className="text-lg font-semibold">Menu</div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle Menu"
            className="z-40"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        {mounted &&
          (
            <div className={`md:hidden px-4 py-4 transition-all duration-300 ${menuOpen ? "bg-white bg-opacity-95 border-gray-200 border-b" : "hidden"}`}>
              <ul className="flex flex-col gap-4">
                {links.map((link) => (
                  <li key={link.name}>
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        `block text-center text-lg font-medium ${isActive ? "text-black" : "text-gray-500 hover:text-black"}`
                      }
                    >
                      {link.name}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          )
        }
      </>
    )
  }

  function radixDropDown() {
    return (
      <DropdownMenu.Root open={menuOpen} modal={false}>
        <div className={`md:hidden px-4 py-3 
          transition-all duration-200 ${(menuOpen || (!(isHome && !scrolled))) ? "bg-white border-gray-200 border-b" : "border-transparent"}
          
          `}>
          <div className="flex items-center">
            <DropdownMenu.Trigger className="flex justify-between items-center w-full">
              <div onClick={() => setMenuOpen(!menuOpen)} className="text-lg font-semibold">Menu</div>
              <div
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle Menu"
                className="z-40"
              >
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </div>
            </DropdownMenu.Trigger>
          </div>

          <DropdownMenu.Portal className="">
            <DropdownMenu.Content sideOffset={13}
              className="w-screen left-0 bg-white bg-opacity-95 border-b border-gray-200 text-gray-400 z-40 
              data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            >
              <ul className="flex flex-col gap-4">
                <DropdownMenu.Item>
                  {links.map((link) => (
                    <li key={link.name}>
                      <NavLink
                        to={link.path}
                        className={({ isActive }) =>
                          `flex items-center justify-center text-lg h-11 font-medium ${isActive ? "text-black" : "text-customGray hover:text-black"}`
                        }
                      >
                        {link.name}
                      </NavLink>
                    </li>
                  ))}
                </DropdownMenu.Item>

              </ul>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </div>
      </DropdownMenu.Root>
    )
  }

  return (
    <nav
      className={`fixed w-full z-30 bg-transparent border-transparent`}
    >
      {/* Desktop Nav (centered) */}
      <div className={`hidden md:flex justify-center transition-all duration-300 ${!(isHome && !scrolled) ? "bg-white border-gray-200 border-b" : "bg-transparent border-transparent"}`}> {/* Transitions from transparent to not when scrolling */}
        <ul className="flex gap-12">
          {links.map((link) => (
            link.name === "Gallery" ? (
              <li key="Gallery" className="relative group py-3"
                onMouseEnter={() => setHoverGallery(true)}
                onMouseLeave={() => setHoverGallery(false)}
              >
                <div className="group">{/* Transitions the menu items text when hovered */}
                  <NavLink
                    to={link.path}
                    className={({ isActive }) =>
                      `text-sm font-medium ${isActive ? "text-black" : `hover:text-black ${!(isHome && !scrolled) ? "text-gray-400" : "text-customGray"} `}`
                    }
                  >
                    Gallery
                  </NavLink>

                  {/* Submenu transitions in and out when hover over gallery */}
                  <div className={`transition-all duration-300 absolute top-full left-1/2 -translate-x-1/2 -mt-[8px] text-center z-40 
                    ${!(isHome && !scrolled) ? "bg-white border border-gray-300 shadow-md" : "bg-transparent border-transparent"}
                    ${hoverGallery ? "opacity-100 visible" : "opacity-0 invisible"}
                    `}>
                    <ul className="flex flex-col py-1">
                      <li>
                        <NavLink
                          to="/gallery/portrait-lounge"
                          className={({ isActive }) =>
                            `block px-4 py-2 text-sm ${isActive
                              ? "text-black"
                              : `hover:text-black ${!(isHome && !scrolled)
                                ? "text-gray-400"
                                : "text-customGray"
                              }`
                            }`
                          }
                        >
                          Portraits
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/gallery/engagement-shoot"
                          className={({ isActive }) => `block px-4 py-2 text-sm ${isActive ? "text-black" : `hover:text-black ${!(isHome && !scrolled) ? "text-gray-400" : "text-customGray"} `}`}
                        >
                          Engagement
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/gallery/china-pre-wedding"
                          className={({ isActive }) => `block px-4 py-2 text-sm ${isActive ? "text-black" : `hover:text-black ${!(isHome && !scrolled) ? "text-gray-400" : "text-customGray"} `}`}
                        >
                          China
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          to="/gallery/vietnam-pre-wedding"
                          className={({ isActive }) => `block px-4 py-2 text-sm ${isActive ? "text-black" : `hover:text-black ${!(isHome && !scrolled) ? "text-gray-400" : "text-customGray"} `}`}
                        >
                          Vietnam
                        </NavLink>
                      </li>
                    </ul>
                  </div>
                </div>
              </li>


            ) : (
              <li key={link.name} className="py-3">
                <NavLink
                  to={link.path}
                  className={({ isActive }) =>
                    `text-sm font-medium ${isActive ? "text-black" : `hover:text-black ${!(isHome && !scrolled) ? "text-gray-400" : "text-customGray"} `}`
                  }
                >
                  {link.name}
                </NavLink>
              </li>
            )
          ))}
        </ul>
      </div>
      {radixDropDown()}
    </nav>
  );
}
